import { useTranslation } from 'react-i18next';
import type { Locale } from 'date-fns';
import { capitalize } from '@tectonics/utils/string';
import { addInstantDays, formatInstantDate, getInstantDayOfWeek } from './format-instant';
import { addPayloadDays, formatPayloadDate, getPayloadDayOfWeek } from './format-payload-date';
import { parseInstant, parsePayloadDate } from './parse-payload-date';
import {
  ar,
  enUS,
  es,
  id,
  ms,
  ptBR,
  th,
  tr,
} from 'date-fns/locale';

type UseLocalizedWeekDayNameOptions = {
  date: string;
  capitalized?: boolean;
  skipWeekend?: boolean;
  useShortName?: boolean;
};

type WeekDayReader = {
  parse: (stringDate: string) => Date,
  format: (date: Date, formatString: string, options: { locale?: Locale }) => string,
  getDayOfWeek: (date: Date) => number,
  addDays: (date: Date, amount: number) => Date,
};

const FULL_NAME = 'EEEE';
const ABBREVIATED_NAME = 'EEE';
const SHORT_NAME = 'EEEEEE';
const SUNDAY = 0;
const SATURDAY = 6;

const payloadDateReader: WeekDayReader = {
  parse: parsePayloadDate,
  format: formatPayloadDate,
  getDayOfWeek: getPayloadDayOfWeek,
  addDays: addPayloadDays,
};

const instantReader: WeekDayReader = {
  parse: parseInstant,
  format: formatInstantDate,
  getDayOfWeek: getInstantDayOfWeek,
  addDays: addInstantDays,
};

/** Names the week day of a payload calendar date, as it was sent. */
export function useLocalizedWeekDayName(options: UseLocalizedWeekDayNameOptions) {
  return useWeekDayName(payloadDateReader, options);
}

/** Names the week day a payload timestamp falls on in the shopper's timezone. */
export function useLocalizedInstantWeekDayName(options: UseLocalizedWeekDayNameOptions) {
  return useWeekDayName(instantReader, options);
}

function useWeekDayName(reader: WeekDayReader, {
  date,
  capitalized = true,
  skipWeekend = false,
  useShortName = false,
}: UseLocalizedWeekDayNameOptions) {
  const { i18n: { resolvedLanguage, language } } = useTranslation();

  const currentLanguage = resolvedLanguage ?? language;
  const locale = (currentLanguage && localeMap.get(currentLanguage)) || DEFAULT_LOCALE;
  const parsedDate = reader.parse(date);
  const localizedDate = skipWeekend ? getNextBusinessDay(reader, parsedDate) : parsedDate;

  const weekDay = reader.format(
    localizedDate,
    useShortName ? getShortNameFormat(currentLanguage) : FULL_NAME,
    { locale },
  );

  return capitalized ? capitalize(weekDay) : weekDay;
}

/**
 * date-fns abbreviates week days per locale, and pt-BR's abbreviated width is the
 * full ordinal name (`segunda`); its short width (`seg`) is the abbreviation there.
 */
function getShortNameFormat(language: string | undefined): string {
  return (language && shortNameFormatMap.get(language)) || ABBREVIATED_NAME;
}

function getNextBusinessDay(reader: WeekDayReader, date: Date): Date {
  const dayOfWeek = reader.getDayOfWeek(date);

  if (dayOfWeek === SATURDAY) {
    return reader.addDays(date, 2);
  }

  return dayOfWeek === SUNDAY ? reader.addDays(date, 1) : date;
}

const DEFAULT_LOCALE = enUS;

const shortNameFormatMap = new Map<string, string>([
  ['pt', SHORT_NAME],
]);

const localeMap = new Map<string, Locale>([
  ['ar', ar],
  ['en', enUS],
  ['es', es],
  ['id', id],
  ['ms', ms],
  ['pt', ptBR],
  ['th', th],
  ['tr', tr],
]);
